/*
  Nectar LP proof sections: shared rail (native scroll snap with prev, next and dots)
  and the customer video dialog. Used by lp-difference, lp-review-grid and lp-ugc-rail.
  Loaded with defer by each section; the guard keeps it to one definition.
*/
(() => {
  if (window.customElements.get('lp-proof-rail')) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LpProofRail extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loop === 'true') {
        this.connectLoop();
        return;
      }
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      if (!this.slides.length) return;
      this.align = this.dataset.align === 'start' ? 'start' : 'center';
      this.index = this.dataset.start === 'middle' ? Math.floor((this.slides.length - 1) / 2) : 0;
      this.label = this.dataset.slideLabel || 'Go to slide';

      this.prevBtn = this.querySelector('[data-rail-prev]');
      this.nextBtn = this.querySelector('[data-rail-next]');
      this.dotsWrap = this.querySelector('[data-rail-dots]');

      this.buildDots();
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

      let frame = 0;
      this.track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => this.sync());
      }, { passive: true });

      this.lastWidth = 0;
      this.observer = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        this.syncFocusable();
        this.go(this.index, true);
      });
      this.observer.observe(this.track);
      this.updateDots();
    }

    disconnectedCallback() {
      if (this.loopEvents) {
        this.loopEvents.abort();
        window.clearTimeout(this.loopTimer);
        window.cancelAnimationFrame(this.loopFrame);
        this.querySelectorAll('[data-rail-clone]').forEach((slide) => slide.remove());
      }
      if (this.observer) this.observer.disconnect();
    }

    /* Only the UGC rail loops. Repeated buffers keep both viewport edges filled;
       rebasing by whole sets after a gesture leaves the visible pictures unchanged. */
    connectLoop() {
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      if (!this.slides.length) return;
      this.align = 'center';
      this.index = this.dataset.start === 'middle' ? Math.floor((this.slides.length - 1) / 2) : 0;
      this.label = this.dataset.slideLabel || 'Go to slide';
      this.dotsWrap = this.querySelector('[data-rail-dots]');
      this.loopEvents = new AbortController();
      const signal = this.loopEvents.signal;
      this.slides.forEach((slide, i) => {
        slide.dataset.railIndex = i;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `Video ${i + 1} of ${this.slides.length}`);
      });
      this.buildDots();
      this.querySelector('[data-rail-prev]')?.addEventListener('click', (event) => this.loopStep(-1, event.detail === 0), { signal });
      this.querySelector('[data-rail-next]')?.addEventListener('click', (event) => this.loopStep(1, event.detail === 0), { signal });
      this.track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') this.loopGo(event.key === 'Home' ? 0 : this.slides.length - 1, true);
        else this.loopStep(event.key === 'ArrowLeft' ? -1 : 1, true);
      }, { signal });
      this.track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(this.loopFrame);
        this.loopFrame = window.requestAnimationFrame(() => this.loopSync());
        this.loopSettle();
      }, { passive: true, signal });
      this.track.addEventListener('pointerdown', () => {
        this.loopPointer = true;
        this.loopTarget = null;
        window.clearTimeout(this.loopTimer);
      }, { passive: true, signal });
      const release = () => { this.loopPointer = false; this.loopSettle(); };
      window.addEventListener('pointerup', release, { passive: true, signal });
      window.addEventListener('pointercancel', release, { passive: true, signal });
      this.track.addEventListener('wheel', () => { this.loopTarget = null; }, { passive: true, signal });
      this.lastSize = '';
      this.observer = new ResizeObserver(() => {
        const size = `${this.track.clientWidth}:${this.slides[0].getBoundingClientRect().width}`;
        if (size === this.lastSize || !this.track.clientWidth) return;
        this.lastSize = size;
        this.loopBuild();
      });
      this.observer.observe(this.track);
      this.observer.observe(this.slides[0]);
    }

    loopBuild() {
      window.clearTimeout(this.loopTimer);
      this.track.querySelectorAll('[data-rail-clone]').forEach((slide) => slide.remove());
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const setWidth = this.slides.reduce((sum, slide) => sum + slide.getBoundingClientRect().width + gap, 0);
      // At least one viewport plus a complete sequence beyond either end.
      const sets = Math.ceil(this.track.clientWidth / setWidth) + 2;
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      for (let n = 0; n < sets; n += 1) {
        [before, after].forEach((fragment) => this.slides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.dataset.railClone = 'true';
          [clone, ...clone.querySelectorAll('*')].forEach((element) => {
            Array.from(element.attributes).forEach((attribute) => {
              if (attribute.name === 'id' || attribute.name.startsWith('data-shopify')) element.removeAttribute(attribute.name);
            });
          });
          // Copies are decoration for sighted pointer users: hidden from assistive
          // technology and out of the tab order, but their play buttons still
          // open the video on click or tap. The real cards stay reachable.
          clone.removeAttribute('role');
          clone.removeAttribute('aria-label');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('button, a[href], [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
          fragment.appendChild(clone);
        }));
      }
      this.track.prepend(before);
      this.track.append(after);
      this.loopSlides = Array.from(this.track.children);
      // Centred cards can leave the gap between two cards, or a sliver of one, at
      // both viewport edges (19px of white at 1366). Then shift the snap point by
      // half a card pitch so each edge cuts through a card. scroll-padding moves
      // the native snap point by the same amount, so swipes snap there too.
      const cardWidth = this.slides[0].getBoundingClientRect().width;
      const pitch = cardWidth + gap;
      const edge = ((((this.track.clientWidth - cardWidth) / 2) % pitch) + pitch) % pitch;
      this.loopOffset = edge < gap + 12 ? pitch / 2 : 0;
      this.track.style.scrollPaddingLeft = this.loopOffset ? `${this.loopOffset * 2}px` : '';
      this.loopTarget = null;
      this.track.setAttribute('tabindex', '0');
      this.loopJump(this.loopPosition(this.slides[this.index]));
      this.loopSync();
    }

    loopPosition(slide) {
      return slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft
        - (this.track.clientWidth - slide.getBoundingClientRect().width) / 2 - (this.loopOffset || 0);
    }

    loopNearest() {
      return this.loopSlides.reduce((best, slide) =>
        Math.abs(this.loopPosition(slide) - this.track.scrollLeft) < Math.abs(this.loopPosition(best) - this.track.scrollLeft) ? slide : best);
    }

    loopJump(left) {
      // Cancel any in-flight native smooth scroll before the invisible whole-set move.
      this.track.scrollTo({ left, behavior: 'instant' });
    }

    loopSync() {
      if (!this.loopSlides) return;
      const nearest = this.loopNearest();
      this.index = Number(nearest.dataset.railIndex);
      this.updateDots();
      // A long uninterrupted wheel/trackpad gesture must not reach a physical
      // buffer edge while waiting for scroll to settle.
      if (!this.loopTarget && (this.track.scrollLeft < this.track.clientWidth
        || this.track.scrollLeft > this.maxScroll() - this.track.clientWidth)) this.loopRebase();
    }

    loopSettle() {
      window.clearTimeout(this.loopTimer);
      this.loopTimer = window.setTimeout(() => {
        if (this.loopPointer || !this.loopSlides) return;
        this.loopSync();
        this.loopTarget = null;
        this.loopRebase();
      }, 160);
    }

    loopRebase() {
      const nearest = this.loopNearest();
      const shift = this.loopPosition(this.slides[Number(nearest.dataset.railIndex)]) - this.loopPosition(nearest);
      if (Math.abs(shift) < 1) return;
      // Focus can only sit on a real card, and the real cards never move in the
      // DOM, so the rebase leaves keyboard focus where it is.
      this.loopJump(this.track.scrollLeft + shift);
      this.loopSync();
    }

    loopStep(direction, instant) {
      if (!this.loopSlides) return;
      const current = this.loopTarget || this.loopNearest();
      let position = this.loopSlides.indexOf(current) + direction;
      // Rapid repeated arrow presses can exhaust a buffer before scroll settles.
      if (position < 1 || position >= this.loopSlides.length - 1) {
        this.loopJump(this.loopPosition(this.slides[Number(current.dataset.railIndex)]));
        position = this.loopSlides.indexOf(this.slides[Number(current.dataset.railIndex)]) + direction;
      }
      this.loopMove(this.loopSlides[position], instant);
    }

    loopGo(index, instant) {
      if (!this.loopSlides) return;
      const candidates = this.loopSlides.filter((slide) => Number(slide.dataset.railIndex) === index);
      const nearest = candidates.reduce((best, slide) =>
        Math.abs(this.loopPosition(slide) - this.track.scrollLeft) < Math.abs(this.loopPosition(best) - this.track.scrollLeft) ? slide : best);
      this.loopMove(nearest, instant);
    }

    loopMove(slide, instant) {
      this.loopTarget = slide;
      this.track.scrollTo({ left: this.loopPosition(slide), behavior: instant || reduceMotion() ? 'instant' : 'smooth' });
      this.loopSettle();
    }

    scrollable() {
      return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    /* A track that cannot scroll (desktop grids) is not a tab stop. */
    syncFocusable() {
      if (this.scrollable()) this.track.setAttribute('tabindex', '0');
      else this.track.removeAttribute('tabindex');
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /* Scroll position that shows a slide, clamped to the track's real range, so
       the end slides on a rail padded to the container edge map to the ends. */
    targetLeft(slide) {
      let left;
      if (this.align === 'start') {
        const pad = parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
        left = slide.offsetLeft - pad;
      } else {
        left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      }
      return Math.min(Math.max(0, left), this.maxScroll());
    }

    /* Arrows move to the next slide that changes the view, so no tap is dead. */
    step(dir) {
      const count = this.slides.length;
      if (!this.scrollable()) {
        this.go(this.index + dir);
        return;
      }
      const here = this.programmatic ? this.targetLeft(this.slides[this.index]) : this.track.scrollLeft;
      let i = this.index;
      for (let n = 0; n < count; n += 1) {
        i = (((i + dir) % count) + count) % count;
        if (Math.abs(this.targetLeft(this.slides[i]) - here) > 2) break;
      }
      this.go(i);
    }

    /* The dots sit 12px apart but each has a 44px hit area, so neighbouring hit
       areas overlap. A pointer tap picks the dot whose centre is nearest. */
    nearestDot(x, fallback) {
      if (!this.dots || !this.dots.length) return fallback;
      let best = fallback;
      let bestDistance = Infinity;
      this.dots.forEach((dot, i) => {
        const r = dot.getBoundingClientRect();
        const distance = Math.abs(r.left + r.width / 2 - x);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    go(i, instant) {
      if (this.dataset.loop === 'true') {
        this.loopGo(i, instant);
        return;
      }
      const count = this.slides.length;
      this.index = ((i % count) + count) % count;
      this.updateDots();
      if (!this.scrollable()) return;
      this.programmatic = true;
      window.clearTimeout(this.programmaticTimer);
      this.programmaticTimer = window.setTimeout(() => { this.programmatic = false; }, instant ? 50 : 700);
      this.track.scrollTo({
        left: Math.max(0, this.targetLeft(this.slides[this.index])),
        behavior: instant || reduceMotion() ? 'auto' : 'smooth'
      });
    }

    sync() {
      if (this.programmatic || !this.scrollable()) return;
      const current = this.track.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(this.targetLeft(slide) - current);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      if (best !== this.index) {
        this.index = best;
        this.updateDots();
      }
    }

    buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lp-proof-rail__dot';
        dot.setAttribute('aria-label', `${this.label} ${i + 1}`);
        dot.addEventListener('click', (event) => {
          this.go(event.detail > 0 ? this.nearestDot(event.clientX, i) : i, event.detail === 0);
        });
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    updateDots() {
      if (!this.dots) return;
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  window.customElements.define('lp-proof-rail', LpProofRail);

  /* Customer video dialog: one shared <dialog>, opened by any [data-lp-video-src] button. */
  let dialog = null;
  let video = null;
  let videoTrigger = null;

  const buildDialog = () => {
    dialog = document.createElement('dialog');
    dialog.className = 'lp-proof-video';
    dialog.setAttribute('aria-label', 'Customer video');
    dialog.innerHTML = '<div class="lp-proof-video__frame">'
      + '<button type="button" class="lp-proof-video__close" aria-label="Close video">'
      + '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>'
      + '</button>'
      + '<video class="lp-proof-video__player" controls playsinline preload="none"></video>'
      + '</div>';
    document.body.appendChild(dialog);
    video = dialog.querySelector('video');
    dialog.querySelector('.lp-proof-video__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      video.pause();
      video.removeAttribute('src');
      video.load();
      const rail = videoTrigger?.closest('lp-proof-rail[data-loop="true"]');
      const source = videoTrigger?.closest('[data-rail-index]');
      if (rail?.slides && source) {
        // A copy is hidden from assistive technology, so focus returns to the
        // matching real card, never to the copy that was clicked.
        const original = rail.slides[Number(source.dataset.railIndex)];
        (original?.querySelector('button') || rail.track).focus({ preventScroll: true });
      }
      videoTrigger = null;
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-lp-video-src]');
    if (!trigger) return;
    const src = trigger.getAttribute('data-lp-video-src');
    if (!src) return;
    event.preventDefault();
    if (!dialog) buildDialog();
    videoTrigger = trigger;
    video.src = src;
    const poster = trigger.getAttribute('data-lp-video-poster');
    if (poster) video.poster = poster;
    else video.removeAttribute('poster');
    dialog.setAttribute('aria-label', trigger.getAttribute('aria-label') || 'Customer video');
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    const playing = video.play();
    if (playing && typeof playing.catch === 'function') playing.catch(() => {});
  });
})();
